/**
 * エラー表示の共通ヘルパー（docs/plan/error-display.md 4.A）
 *
 *   this.$handleError("076", e, { hideLoading: true })
 *   this.$handleError("072", e, { hideLoading: true, messages: { B: "ログインし直してください" } })
 *
 * ① 例外を AppError に正規化 ② 分類文字を判定 ③ 表示文を組み立て
 * ④ Logger.error（本番でも出力） ⑤ GA4 送信 ⑥ hideLoading / hideProgress ⑦ showMessage
 */
import { Logger } from "@/js/utils/Logger.js";
import { Analytics } from "@/js/Analytics.js";
import { AppError, classifyError, buildUserMessage, normalizeSiteNo, isDialogSuppressed } from "@/js/utils/AppError.js";
import { getErrorSite } from "@/js/utils/errorSites.js";

/**
 * 記録のみ（画面には出さない）。グローバルハンドラや、表示を別途行う箇所から使う。
 * @param {string|number} siteNo 番号（"076"）
 * @param {*} error
 * @param {object} [meta] origin（"Vue2" / "window.error" / "unhandledrejection" / "handled"）、info など
 * @returns {{ fullCode: string, cls: string, appError: AppError, site: object }}
 */
export function reportError(siteNo, error, meta = {}) {
  const no = normalizeSiteNo(siteNo);
  const site = getErrorSite(no);
  const appError = AppError.from(error);
  const cls = classifyError(appError);
  const fullCode = `${cls}-${no}`;
  const origin = meta.origin || "handled";

  Logger.error(`[${fullCode}] ${site.feature} ${site.label}`.trim(), appError.summary(), appError.cause);
  Analytics.exception(fullCode, appError, site, { origin, info: meta.info });

  return { fullCode, cls, appError, site };
}

/**
 * 記録して画面に表示する。
 * @param {object} store Vuex store
 * @param {string|number} siteNo 番号（"076"）
 * @param {*} error
 * @param {object} [options]
 * @param {boolean} [options.hideLoading]  showMessage の前に hideLoading を commit
 * @param {boolean} [options.hideProgress] showMessage の前に hideProgress を commit
 * @param {string}  [options.message]      分類に関係なく使う本文（行動文込み）。コード行は自動で付く
 * @param {object}  [options.messages]     分類ごとの本文 { A: "...", B: "..." }。message より弱く、Auth の文言表より強い
 * @returns {{ fullCode: string, cls: string, userMessage: string, appError: AppError, suppressed: boolean }}
 */
export function handleError(store, siteNo, error, options = {}) {
  const hide = () => {
    if (!store) return;
    if (options.hideLoading) store.commit("hideLoading");
    if (options.hideProgress) store.commit("hideProgress");
  };

  // ユーザーが自分で中断した操作（ポップアップを閉じた等）は失敗として扱わず、ダイアログも記録も出さない
  const probe = AppError.from(error);
  if (isDialogSuppressed(probe)) {
    Logger.debug(`[${normalizeSiteNo(siteNo)}] suppressed`, probe.summary());
    hide();
    return { fullCode: "", cls: "", userMessage: "", appError: probe, suppressed: true };
  }

  const { fullCode, cls, appError, site } = reportError(siteNo, error, { origin: "handled" });
  const perClass = options.messages && options.messages[cls];
  const userMessage = buildUserMessage(cls, fullCode, options.message || perClass, appError);

  hide();
  if (store) store.commit("showMessage", userMessage);

  return { fullCode, cls, userMessage, appError, site, suppressed: false };
}
